import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Alert } from "./alert";

interface SummaryType {
  total: number;
  inbox: {
    count: number;
    percentage: number;
  };
  spam: {
    count: number;
    percentage: number;
  };
  unreceived: {
    count: number;
    percentage: number;
  };
}

interface ProcessingProgressProps {
  initialProgress?: number;
  maxTimeInHours?: number;
  testId?: string;
  isNotCompleted?: () => boolean;
  onTimeout?: () => void;
  summary?: SummaryType;
}

export function ProcessingProgress({
  initialProgress = 0,
  maxTimeInHours = 1,
  testId,
  isNotCompleted,
  onTimeout,
  summary,
}: ProcessingProgressProps) {
  // Define constants
  const REFETCH_INTERVAL_MS = 10000;

  // If data is missing, don't render
  if (!summary || !isNotCompleted || !isNotCompleted()) {
    return null;
  }
  
  // Calculate completion percentage
  const completionPercentage = Math.min(
    Math.round(
      ((summary.total - summary.unreceived.count) / 
        (summary.total || 1)) * 100
    ),
    99
  );

  return (
    <Alert variant="info" className="flex flex-col items-start">
      {/* Enhanced progress bar */}
      <div className="w-full bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
        <div className="flex items-center mb-2 gap-2">
          <div className="relative h-4 w-4 flex-shrink-0">
            <div className="absolute animate-ping inline-flex h-full w-full rounded-full bg-blue-400 opacity-30"></div>
            <div className="relative flex rounded-full h-4 w-4 bg-blue-500 items-center justify-center">
              <Loader2 className="h-3 w-3 text-white animate-spin" />
            </div>
          </div>
          <div className="text-sm font-medium text-blue-700 dark:text-blue-300">
            Processing data from email providers
          </div>
          <div className="ml-auto text-sm font-bold text-blue-700 dark:text-blue-300">
            {completionPercentage}%
          </div>
        </div>

        <div className="w-full h-3 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-400 rounded-full transition-all duration-700 ease-in-out relative"
            style={{
              width: `${completionPercentage}%`,
            }}
          >
            <div className="absolute inset-0 bg-white/20 w-full h-full shine-animation"></div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between mt-2 text-xs text-blue-600 dark:text-blue-400">
          <div className="flex items-center gap-1">
            <span>Processed:</span>
            <span className="font-medium">
              {summary.total - summary.unreceived.count}
            </span>
            <span>of</span>
            <span className="font-medium">
              {summary.total}
            </span>
            <span>providers</span>
          </div>
          <div className="flex items-center gap-1">
            <span>Auto-updating in</span>
            <span className="font-medium">
              {REFETCH_INTERVAL_MS / 1000}s
            </span>
          </div>
        </div>
      </div>
    </Alert>
  );
} 